import SendIcon from '@mui/icons-material/Send';
import { Box, IconButton, Paper, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useCreateCompanyChatMutation, useGetCompanyChatQuery, useGetCompanyChatsQuery, useSendCompanyChatMessageMutation } from '../../../api/dieter/companyChat/hooks';
import { useUser } from '../../../context/UserContext';
import { useNavigation } from '../../../hooks/useNavigation';

interface DpoChatProps {
  taskId: string;
  chatId?: string;
  companyId: string;
}

export function DpoChat({ taskId, chatId: initialChatId, companyId }: DpoChatProps) {
  const { user } = useUser();
  const { navigation, setNavigation } = useNavigation();
  const [chatId, setChatId] = useState<string | null>(initialChatId || null);
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [createChat, { loading: createLoading }] = useCreateCompanyChatMutation();
  const [sendMessage, { loading: sendLoading }] = useSendCompanyChatMessageMutation();
  const { data: chatsData, loading: chatsLoading } = useGetCompanyChatsQuery();
  const { data: chatData, loading: chatDataLoading, refetch } = useGetCompanyChatQuery(chatId || '', {
    skip: !chatId,
  });

  const chat = chatData?.getCompanyChat;

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat?.messages]);

  // Mark messages as read when viewing the chat
  useEffect(() => {
    if (chatId && chat?.messages && chat.messages.length > 0) {
      const lastMessage = chat.messages[chat.messages.length - 1];
      const lastReadMessageId = navigation.chatLastReadMessageIds?.[chatId];

      // Only update if the last message has changed
      if (lastMessage.id !== lastReadMessageId) {
        setNavigation((nav) => {
          if (!nav.chatLastReadMessageIds) {
            nav.chatLastReadMessageIds = {};
          }
          nav.chatLastReadMessageIds[chatId] = lastMessage.id;
        });
      }
    }
  }, [chatId, chat?.messages, setNavigation, navigation.chatLastReadMessageIds]);

  // Poll for new messages every 2 seconds
  useEffect(() => {
    if (!chatId) return;
    const interval = setInterval(() => {
      refetch();
    }, 2000);
    return () => clearInterval(interval);
  }, [chatId, refetch]);

  // Check for existing chat when component mounts
  useEffect(() => {
    if (!initialChatId && !chatId && chatsData?.getCompanyChats && companyId && taskId) {
      // Find existing chat for this specific task (identified by title pattern)
      const chatTitlePattern = `DPO Task: ${taskId}`;
      const existingChat = chatsData.getCompanyChats.find(
        (c: any) => c.companyId === companyId && c.title?.includes(chatTitlePattern)
      );
      if (existingChat) {
        setChatId(existingChat.id);
      }
    }
  }, [chatsData, companyId, taskId, initialChatId, chatId]);

  const handleSend = async () => {
    if (!message.trim()) return;

    const content = message;
    setMessage('');

    try {
      let targetChatId = chatId;

      // Create chat if it doesn't exist
      if (!targetChatId) {
        const result = await createChat({
          variables: {
            title: `DPO Task: ${taskId} - ${new Date().toLocaleDateString()}`,
          },
        });
        targetChatId = result.data?.createCompanyChat?.id;
        if (targetChatId) {
          setChatId(targetChatId);
        }
      }

      // Send the message
      if (targetChatId) {
        await sendMessage({
          variables: {
            chatId: targetChatId,
            content,
          },
        });
        
        // Refetch immediately after sending
        await refetch();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessage(content); // Restore message on error
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (companyId === '') {
    return <Typography color="error" sx={{ p: 2 }}>Company ID is required to load chat</Typography>;
  }

  // Show loading state while checking for existing chats
  if (chatsLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, border: 2, borderColor: '#E3E3E3' }}>
        <Box sx={{ py: '12px', px: '24px', borderBottom: 2, borderColor: '#E3E3E3', flexShrink: 0, bgcolor: '#ffffff' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333', textAlign: 'center' }}>Chat</Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, border: 2, borderColor: '#E3E3E3' }}>
      <Box sx={{ py: '12px', px: '24px', borderBottom: 2, borderColor: '#E3E3E3', flexShrink: 0, bgcolor: '#ffffff' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333', textAlign: 'center' }}>Chat</Typography>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minHeight: 0,
          bgcolor: '#f5f5f5',
        }}
      >
        {chat?.messages && chat.messages.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Noch keine Chatnachrichten.
            </Typography>
          </Box>
        )}

        {chat?.messages?.map((msg: any) => {
          const isOwnMessage = msg.senderId === user?.id;
          const senderName = msg.sender?.firstName || msg.sender?.lastName
            ? `${msg.sender?.firstName || ''} ${msg.sender?.lastName || ''}`.trim()
            : msg.sender?.email || 'Unknown';
          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                gap: 1,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  maxWidth: '70%',
                  bgcolor: isOwnMessage ? '#ffffff' : '#f5f5f5',
                  border: 'none',
                }}
              >
                {!isOwnMessage && (
                  <Typography variant="caption" fontWeight="bold" display="block">
                    {senderName}
                  </Typography>
                )}
                <Typography variant="body2">{msg.content}</Typography>
              </Paper>
            </Box>
          );
        })}

        {!chat && !chatsLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Noch keine Chatnachrichten.
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
            sx={{
              p: 1,
              borderTop: 2,
              borderColor: '#E3E3E3',
              display: 'flex',
              gap: 1,
              flexShrink: 0,
              bgcolor: '#ffffff',
            }}
          >
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              // TODO: i18next translation
              placeholder="Text eingeben..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={createLoading || sendLoading}
              variant="standard"
              sx={{
                '& .MuiInput-underline:before': {
                  borderBottom: 'none !important',
                },
                '& .MuiInput-underline:hover:before': {
                  borderBottom: 'none !important',
                },
                '& .MuiInput-underline:after': {
                  borderBottom: 'none !important',
                },
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: 0,
                },
              }}
            />
            <IconButton 
              color="primary" 
              onClick={handleSend} 
              disabled={!message.trim() || createLoading || sendLoading}
            >
              <SendIcon />
            </IconButton>
          </Box>
    </Box>
  );
}
